/* All styles are injected into the widget Shadow DOM to avoid host-page CSS leakage. */
/*
 * Uses a system-font stack instead of loading Google Fonts. Remote webfonts
 * (fonts.googleapis.com) would leak every visitor's IP to Google from every
 * host page, which is a GDPR concern for an embeddable widget. If a host
 * page already loads "Inter", the widget will pick it up automatically.
 */

:host {
  all: initial;
  color-scheme: light dark;
  display: block;
  font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 16px;
  line-height: 1.5;
  text-size-adjust: 100%;
  -webkit-text-size-adjust: 100%;
}

.fdb-widget {
  --fdb-accent: #7466FF;
  --fdb-accent-secondary: #C15CFF;
  --fdb-accent-glow: rgba(116, 102, 255, 0.25);
  --fdb-bg-deep: #0B1121;
  --fdb-bg-panel: #0F172A;
  --fdb-bg-surface: #1E293B;
  --fdb-bg-elevated: #253348;
  --fdb-border: #334155;
  --fdb-border-subtle: #1E293B;
  --fdb-text-primary: #F8FAFC;
  --fdb-text-secondary: #94A3B8;
  --fdb-text-muted: #64748B;
  --fdb-text-on-accent: #FFFFFF;
  --fdb-shadow: 0 12px 48px rgba(0, 0, 0, 0.5), 0 4px 16px rgba(0, 0, 0, 0.3);
  --fdb-radius: 16px;
  --fdb-radius-sm: 10px;
  --fdb-radius-xs: 6px;
  --fdb-transition: 300ms cubic-bezier(0.4, 0, 0.2, 1);
  color: var(--fdb-text-primary);
  font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 16px;
  line-height: 1.5;
}

.fdb-widget * {
  box-sizing: border-box;
  font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  margin: 0;
  padding: 0;
}

.fdb-widget button,
.fdb-widget input,
.fdb-widget textarea {
  font: inherit;
  letter-spacing: normal;
  text-transform: none;
}

/* ── FAB Button ─────────────────────────────────────── */
.fdb-fab {
  position: fixed;
  bottom: 24px;
  bottom: max(24px, env(safe-area-inset-bottom, 0px));
  width: 68px;
  height: 68px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--fdb-accent), var(--fdb-accent-secondary));
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 20px var(--fdb-accent-glow), 0 2px 8px rgba(0, 0, 0, 0.3);
  transition: transform var(--fdb-transition), box-shadow var(--fdb-transition);
  z-index: 9999;
  animation: fdb-pulse 3s ease-in-out infinite;
}
.fdb-widget--bottom-right .fdb-fab { right: 24px; }
.fdb-widget--bottom-left .fdb-fab { left: 24px; }

.fdb-fab:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 32px var(--fdb-accent-glow), 0 4px 12px rgba(0, 0, 0, 0.4);
  animation: none;
}
.fdb-fab--open {
  animation: none;
  background: linear-gradient(135deg, #475569, #334155);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}
.fdb-fab--open:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
}

.fdb-fab svg {
  transition: transform var(--fdb-transition), opacity 200ms ease;
}
.fdb-fab--open svg {
  transform: rotate(90deg);
}

@keyframes fdb-pulse {
  0%, 100% { box-shadow: 0 4px 20px var(--fdb-accent-glow), 0 2px 8px rgba(0, 0, 0, 0.3); }
  50% { box-shadow: 0 4px 32px rgba(116, 102, 255, 0.5), 0 2px 8px rgba(0, 0, 0, 0.3); }
}

@media (prefers-reduced-motion: reduce) {
  .fdb-fab { animation: none; }
  .fdb-fab:hover { transform: none; }
  .fdb-fab svg { transition: none; }
}

/* ── Panel ──────────────────────────────────────────── */
.fdb-panel {
  position: fixed;
  bottom: 104px;
  bottom: calc(max(24px, env(safe-area-inset-bottom, 0px)) + 80px);
  width: min(440px, calc(100vw - 48px));
  height: min(640px, calc(100vh - 128px));
  max-height: calc(100vh - 128px);
  background: var(--fdb-bg-panel);
  border: 1px solid var(--fdb-border);
  border-radius: var(--fdb-radius);
  box-shadow: var(--fdb-shadow);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 9998;
  animation: fdb-panel-open 350ms cubic-bezier(0.34, 1.56, 0.64, 1);
  transform-origin: bottom right;
}
.fdb-widget--bottom-right .fdb-panel { right: 24px; }
.fdb-widget--bottom-left .fdb-panel { left: 24px; transform-origin: bottom left; }

@supports (height: 100dvh) {
  .fdb-panel {
    height: min(640px, calc(100dvh - 128px));
    max-height: calc(100dvh - 128px);
  }
}

@keyframes fdb-panel-open {
  from { opacity: 0; transform: scale(0.85) translateY(20px); }
  to { opacity: 1; transform: scale(1) translateY(0); }
}

@media (prefers-reduced-motion: reduce) {
  .fdb-panel { animation: none; }
}

@media (max-width: 768px) {
  .fdb-fab {
    bottom: 16px;
    bottom: max(16px, env(safe-area-inset-bottom, 0px));
    width: 58px;
    height: 58px;
  }
  .fdb-widget--bottom-right .fdb-fab {
    right: 16px;
    right: max(16px, env(safe-area-inset-right, 0px));
  }
  .fdb-widget--bottom-left .fdb-fab {
    left: 16px;
    left: max(16px, env(safe-area-inset-left, 0px));
  }

  .fdb-panel {
    top: 10px;
    top: max(10px, env(safe-area-inset-top, 0px));
    bottom: 86px;
    bottom: calc(max(16px, env(safe-area-inset-bottom, 0px)) + 70px);
    width: auto;
    height: auto;
    max-height: none;
    right: 8px !important;
    right: max(8px, env(safe-area-inset-right, 0px)) !important;
    left: 8px !important;
    left: max(8px, env(safe-area-inset-left, 0px)) !important;
    border-radius: var(--fdb-radius-sm);
  }
}

/* ── Panel Header ───────────────────────────────────── */
.fdb-panel__header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  background: linear-gradient(180deg, var(--fdb-bg-surface) 0%, var(--fdb-bg-panel) 100%);
  border-bottom: 1px solid var(--fdb-border);
  flex-shrink: 0;
}
.fdb-panel__logo {
  display: flex;
  align-items: center;
  position: relative;
  flex-shrink: 0;
}
.fdb-panel__logo svg,
.fdb-panel__logo img,
.fdb-panel__logo-text {
  border-radius: 50%;
  box-shadow: 0 0 0 2px var(--fdb-border);
}
/* Enforce a uniform 28×28 logo footprint so all HeaderLogo variants
   (FalkorDBLogo SVG, fallback GenericChatIcon SVG, URL <img>, emoji/text
   span) share the same size — the ::after status dot is positioned
   relative to a 28×28 logo box, so a smaller child would shrink the
   ring and misalign the dot. CSS overrides the SVG width/height
   attribute, so even if a future inline SVG ships at a different
   intrinsic size it'll still render at 28×28 here. */
.fdb-panel__logo svg {
  width: 28px;
  height: 28px;
}
.fdb-panel__logo img {
  width: 28px;
  height: 28px;
  object-fit: cover;
  display: block;
}
.fdb-panel__logo-text {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--fdb-bg-surface);
  color: var(--fdb-text-primary);
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
  overflow: hidden;
}
.fdb-panel__logo::after {
  content: '';
  position: absolute;
  bottom: 0;
  right: -1px;
  width: 9px;
  height: 9px;
  background: var(--fdb-accent);
  border-radius: 50%;
  border: 2px solid var(--fdb-bg-surface);
}
.fdb-panel__title-group {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.fdb-panel__title {
  font-size: 14px;
  font-weight: 600;
  color: var(--fdb-text-primary);
  letter-spacing: -0.01em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
/* Optional one-line description shown under the title — set per-graph
   in Widget appearance > Description on the SPA. Truncates so a long string
   doesn't bloat the panel header. */
.fdb-panel__description {
  font-size: 11px;
  font-weight: 400;
  color: var(--fdb-text-secondary, rgba(255, 255, 255, 0.55));
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ── Panel Body ─────────────────────────────────────── */
.fdb-panel__body {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* ── Buttons ────────────────────────────────────────── */
.fdb-btn {
  border: none;
  cursor: pointer;
  transition: all 200ms ease;
  font-family: inherit;
}
.fdb-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.fdb-btn--icon {
  background: transparent;
  color: var(--fdb-text-secondary);
  padding: 6px;
  border-radius: var(--fdb-radius-xs);
  display: flex;
  align-items: center;
}
.fdb-btn--icon:hover { color: var(--fdb-text-primary); background: var(--fdb-bg-elevated); }
.fdb-btn--close:hover { color: #EF4444; background: rgba(239, 68, 68, 0.1); }
.fdb-btn--primary {
  background: linear-gradient(135deg, var(--fdb-accent), var(--fdb-accent-secondary));
  color: var(--fdb-text-on-accent);
  font-weight: 600;
  font-size: 14px;
  padding: 11px 16px;
  border-radius: var(--fdb-radius-sm);
}
.fdb-btn--primary:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
.fdb-btn--primary:active:not(:disabled) { transform: translateY(0); }
.fdb-btn--full { width: 100%; }
.fdb-btn--secondary {
  background: var(--fdb-bg-surface);
  color: var(--fdb-text-primary);
  border: 1px solid var(--fdb-border);
  font-size: 13px;
  padding: 8px 14px;
  border-radius: var(--fdb-radius-sm);
  margin-top: 12px;
}
.fdb-btn--secondary:hover { background: var(--fdb-bg-elevated); }
.fdb-btn--ghost {
  background: transparent;
  color: var(--fdb-text-muted);
  font-size: 13px;
  padding: 4px 0;
}
.fdb-btn--ghost:hover { color: var(--fdb-accent); }
.fdb-btn--inline {
  display: inline-block;
  margin-top: 8px;
  background: rgba(116, 102, 255, 0.08);
  color: var(--fdb-accent);
  font-size: 12px;
  font-weight: 500;
  padding: 5px 12px;
  border: 1px solid rgba(116, 102, 255, 0.25);
  border-radius: 100px;
}
.fdb-btn--inline:hover { background: rgba(116, 102, 255, 0.15); border-color: var(--fdb-accent); }
.fdb-btn--send {
  background: linear-gradient(135deg, var(--fdb-accent), var(--fdb-accent-secondary));
  color: var(--fdb-text-on-accent);
  width: 38px;
  height: 38px;
  border-radius: var(--fdb-radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.fdb-btn--send:hover:not(:disabled) { opacity: 0.9; transform: scale(1.05); }
.fdb-btn--send:active:not(:disabled) { transform: scale(0.95); }

/* ── Inputs ─────────────────────────────────────────── */
.fdb-input {
  width: 100%;
  background: var(--fdb-bg-deep);
  border: 1px solid var(--fdb-border);
  border-radius: var(--fdb-radius-sm);
  color: var(--fdb-text-primary);
  font-size: 14px;
  padding: 10px 12px;
  outline: none;
  transition: border-color 200ms ease, box-shadow 200ms ease;
}
.fdb-input:focus {
  border-color: var(--fdb-accent);
  box-shadow: 0 0 0 3px var(--fdb-accent-glow);
}
.fdb-input::placeholder { color: var(--fdb-text-muted); }
.fdb-input--textarea { resize: vertical; min-height: 80px; }
.fdb-input--chat {
  flex: 1;
  height: 38px;
  padding: 8px 14px;
  border-radius: var(--fdb-radius-sm);
  font-size: 14px;
  background: var(--fdb-bg-surface);
}
.fdb-input__error {
  font-size: 12px;
  color: #EF4444;
  margin-top: -4px;
}

/* ── Labels ─────────────────────────────────────────── */
.fdb-label {
  font-size: 12px;
  font-weight: 500;
  color: var(--fdb-text-secondary);
  margin-bottom: 4px;
  display: block;
}

/* ── Chat ───────────────────────────────────────────── */
.fdb-chat {
  display: flex;
  flex-direction: column;
  height: 100%;
}
.fdb-chat__messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  scrollbar-width: thin;
  scrollbar-color: #334155 transparent;
}
.fdb-chat__messages::-webkit-scrollbar { width: 4px; }
.fdb-chat__messages::-webkit-scrollbar-track { background: transparent; }
.fdb-chat__messages::-webkit-scrollbar-thumb { background: #334155; border-radius: 4px; }

.fdb-chat__welcome {
  text-align: center;
  margin-top: 40px;
  animation: fdb-fade-in 500ms ease;
}
.fdb-chat__welcome-icon {
  width: 106px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 12px;
}
.fdb-chat__welcome-icon svg {
  width: 100%;
  height: 100%;
  display: block;
}
.fdb-chat__welcome-title {
  font-size: 17px;
  font-weight: 700;
  color: var(--fdb-text-primary);
  margin-bottom: 4px;
}
.fdb-chat__welcome-subtitle {
  font-size: 13.5px;
  color: var(--fdb-text-muted);
  line-height: 1.4;
}

@keyframes fdb-fade-in {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
}

.fdb-chat__footer-link {
  padding: 2px 16px 0;
  text-align: center;
}
.fdb-chat__input-bar {
  display: flex;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid var(--fdb-border-subtle);
  background: var(--fdb-bg-panel);
}

/* ── Privacy Notice ─────────────────────────────────── */
.fdb-chat__privacy {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 3px 8px;
  margin: 0 14px 10px;
  padding: 7px 10px;
  background: rgba(15, 23, 42, 0.72);
  border: 1px solid rgba(51, 65, 85, 0.72);
  border-radius: 999px;
  font-size: 11px;
  color: var(--fdb-text-muted);
  text-align: center;
  line-height: 1.25;
}
.fdb-chat__privacy-text {
  color: var(--fdb-text-secondary);
  font-weight: 500;
}
.fdb-chat__privacy-links {
  color: var(--fdb-text-muted);
  white-space: nowrap;
}
.fdb-chat__privacy a {
  color: var(--fdb-accent);
  text-decoration: none;
  font-weight: 600;
}
.fdb-chat__privacy a:hover { color: var(--fdb-accent-secondary); text-decoration: underline; }

/* ── Message Bubbles ────────────────────────────────── */
.fdb-bubble {
  max-width: 85%;
  padding: 10px 14px;
  font-size: 14px;
  line-height: 1.55;
  animation: fdb-msg-in 300ms cubic-bezier(0.34, 1.56, 0.64, 1);
  overflow-wrap: break-word;
}
.fdb-bubble p { margin: 0; }
.fdb-markdown {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.fdb-markdown p {
  margin: 0;
}
.fdb-markdown strong {
  color: var(--fdb-text-primary);
  font-weight: 700;
}
.fdb-markdown a {
  color: var(--fdb-accent);
  text-decoration: underline;
  text-underline-offset: 2px;
}
.fdb-markdown a:hover,
.fdb-markdown a:focus {
  color: var(--fdb-accent-secondary);
}
.fdb-markdown h1,
.fdb-markdown h2,
.fdb-markdown h3,
.fdb-markdown h4,
.fdb-markdown h5,
.fdb-markdown h6 {
  color: var(--fdb-text-primary);
  font-weight: 700;
  line-height: 1.35;
  margin: 0;
}
.fdb-markdown h1,
.fdb-markdown h2 {
  font-size: 1.1em;
}
.fdb-markdown h3,
.fdb-markdown h4 {
  font-size: 1.02em;
}
.fdb-markdown h5,
.fdb-markdown h6 {
  font-size: 1em;
}
.fdb-markdown ul,
.fdb-markdown ol {
  margin: 0;
  padding-left: 18px;
}
.fdb-markdown li + li {
  margin-top: 4px;
}
.fdb-markdown code {
  background: var(--fdb-bg-deep);
  border: 1px solid var(--fdb-border-subtle);
  border-radius: var(--fdb-radius-xs);
  color: var(--fdb-accent);
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.92em;
  padding: 1px 4px;
}
.fdb-markdown pre {
  background: var(--fdb-bg-deep);
  border: 1px solid var(--fdb-border);
  border-radius: var(--fdb-radius-xs);
  color: var(--fdb-text-primary);
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 12px;
  line-height: 1.45;
  margin: 0;
  max-width: 100%;
  overflow-x: auto;
  padding: 10px;
  white-space: pre;
}
.fdb-markdown pre code {
  background: transparent;
  border: 0;
  color: inherit;
  font-family: inherit;
  font-size: inherit;
  padding: 0;
}
.fdb-bubble--user {
  background: linear-gradient(135deg, var(--fdb-accent), var(--fdb-accent-secondary));
  color: var(--fdb-text-on-accent);
  align-self: flex-end;
  font-weight: 500;
  border-radius: var(--fdb-radius-sm) var(--fdb-radius-sm) 4px var(--fdb-radius-sm);
  box-shadow: 0 2px 8px var(--fdb-accent-glow);
}
.fdb-bubble--assistant {
  background: var(--fdb-bg-surface);
  color: var(--fdb-text-primary);
  align-self: flex-start;
  border: 1px solid var(--fdb-border);
  border-radius: var(--fdb-radius-sm) var(--fdb-radius-sm) var(--fdb-radius-sm) 4px;
}

/* ── Cited sources footer ───────────────────────── */
.fdb-sources {
  margin-top: 8px;
  padding-top: 6px;
  border-top: 1px dashed var(--fdb-border-subtle);
  font-size: 11px;
  color: var(--fdb-text-muted);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 6px;
  line-height: 1.4;
}
.fdb-sources__label {
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 600;
  color: var(--fdb-text-secondary);
}
.fdb-sources__list {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
}
.fdb-sources__item {
  background: var(--fdb-bg-elevated);
  border: 1px solid var(--fdb-border-subtle);
  border-radius: var(--fdb-radius-xs);
  padding: 1px 6px;
  color: var(--fdb-text-secondary);
  font-family: inherit;
  white-space: nowrap;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
}
a.fdb-sources__item--link {
  text-decoration: none;
  cursor: pointer;
  transition: color var(--fdb-transition), background var(--fdb-transition), border-color var(--fdb-transition);
}
a.fdb-sources__item--link:hover {
  color: var(--fdb-accent);
  border-color: rgba(116, 102, 255, 0.4);
  background: rgba(116, 102, 255, 0.12);
}
a.fdb-sources__item--link:focus-visible {
  color: var(--fdb-accent);
  border-color: var(--fdb-accent);
  background: rgba(116, 102, 255, 0.12);
  outline: 2px solid var(--fdb-accent);
  outline-offset: 2px;
}

/* ── Like/dislike feedback ──────────────────────── */
.fdb-feedback {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 8px;
  padding-top: 6px;
  border-top: 1px dashed var(--fdb-border-subtle);
}
.fdb-feedback__btn {
  background: transparent;
  border: 1px solid transparent;
  border-radius: var(--fdb-radius-xs);
  padding: 4px 6px;
  cursor: pointer;
  color: var(--fdb-text-muted);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: color var(--fdb-transition), background var(--fdb-transition), border-color var(--fdb-transition);
}
.fdb-feedback__btn:hover:not(:disabled) {
  color: var(--fdb-accent);
  background: rgba(116, 102, 255, 0.1);
}
.fdb-feedback__btn:disabled { cursor: default; }
.fdb-feedback__btn--active {
  color: var(--fdb-accent);
  border-color: rgba(116, 102, 255, 0.4);
  background: rgba(116, 102, 255, 0.12);
}
.fdb-feedback__thanks {
  font-size: 11px;
  color: var(--fdb-text-muted);
  margin-left: 4px;
}

@keyframes fdb-msg-in {
  from { opacity: 0; transform: translateY(8px) scale(0.95); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}
@media (prefers-reduced-motion: reduce) {
  .fdb-bubble { animation: none; }
}

/* ── Typing Indicator ───────────────────────────────── */
.fdb-bubble--typing {
  display: flex;
  gap: 5px;
  align-items: center;
  padding: 14px 18px;
}
.fdb-bubble--typing span {
  display: block;
  width: 7px;
  height: 7px;
  background: var(--fdb-text-muted);
  border-radius: 50%;
  animation: fdb-bounce 1.2s ease-in-out infinite;
}
.fdb-bubble--typing span:nth-child(2) { animation-delay: 0.2s; }
.fdb-bubble--typing span:nth-child(3) { animation-delay: 0.4s; }

@keyframes fdb-bounce {
  0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
  30% { transform: translateY(-6px); opacity: 1; }
}
@media (prefers-reduced-motion: reduce) {
  .fdb-bubble--typing span { animation: none; opacity: 0.5; }
}

/* ── Suggestion Cards ───────────────────────────────── */
.fdb-suggestions {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 4px 0 8px;
  margin-top: 4px;
  animation: fdb-fade-in 400ms ease 200ms both;
}
.fdb-suggestions__label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--fdb-text-secondary);
  opacity: 0.6;
  padding: 0 2px 4px;
}
.fdb-suggestion-card {
  display: block;
  width: 100%;
  background: var(--fdb-bg-surface);
  border: 1px solid var(--fdb-border);
  color: var(--fdb-text-primary);
  font-size: 13.5px;
  text-align: left;
  padding: 9px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 160ms ease, background 160ms ease;
  font-family: inherit;
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.fdb-suggestion-card:hover {
  border-color: var(--fdb-accent);
  background: rgba(116, 102, 255, 0.06);
}

/* ── Support Form ───────────────────────────────────── */
.fdb-support {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
}
.fdb-support__header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--fdb-border-subtle);
  flex-shrink: 0;
}
.fdb-support__title {
  font-size: 14px;
  font-weight: 600;
  color: var(--fdb-text-primary);
}
.fdb-support__form {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  flex: 1;
  animation: fdb-fade-in 300ms ease;
}
.fdb-support__error {
  font-size: 12px;
  color: #EF4444;
}
.fdb-support--success {
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 32px 24px;
  gap: 12px;
  animation: fdb-fade-in 400ms ease;
}
.fdb-support__checkmark {
  margin-bottom: 8px;
  animation: fdb-check-pop 500ms cubic-bezier(0.34, 1.56, 0.64, 1);
}
@keyframes fdb-check-pop {
  from { transform: scale(0.5); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}
.fdb-support__success-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--fdb-text-primary);
}
.fdb-support__success-body {
  font-size: 13px;
  color: var(--fdb-text-secondary);
  line-height: 1.5;
}
